import React, { useState, useEffect } from 'react';
import OurLayouts from '../layout/index';
import { FaStar } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const FeedbackPage = () => {
  const userRole = localStorage.getItem("role");
  const [activeTab, setActiveTab] = useState('clinic'); // 'clinic' or 'consultation'
  const [feedbackType, setFeedbackType] = useState('');
  const [subject, setSubject] = useState('');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    totalPages: 0,
    totalRecords: 0,
    pageSize: 10
  });
  
  // Date range for consultation feedback
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
      fromDate: thirtyDaysAgo.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
  };
  const [fromDate, setFromDate] = useState(getDefaultDates().fromDate);
  const [toDate, setToDate] = useState(getDefaultDates().toDate);

  const [experienceRatings, setExperienceRatings] = useState({
    systemReliability: 0,
    userInterface: 0,
    patientFlow: 0,
    bookingAppointments: 0,
    orderPlacement: 0,
    overallExperience: 0
  });

  useEffect(() => {
    // If user role is USER, ensure activeTab is clinic
    if (userRole === 'USER' && activeTab === 'consultation') {
      setActiveTab('clinic');
    }
  }, [userRole, activeTab]);

  useEffect(() => {
    // Reset pagination when switching tabs or changing dates
    setPagination({
      pageNumber: 0,
      totalPages: 0,
      totalRecords: 0,
      pageSize: 10
    });
    fetchFeedbacks(0, 10);
  }, [activeTab, fromDate, toDate]);

  const fetchFeedbacks = async (page = 0, size = 10) => {
    try {
      setLoadingFeedbacks(true);
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      const API_BASE = process.env.REACT_APP_API_URL;
      
      let response;
      
      if (activeTab === 'consultation') {
        // Use consultation feedback endpoint with date range
        response = await axios.get(`${API_BASE}consultation/feedback/list?fromDate=${fromDate}&toDate=${toDate}&page=${page}&size=${size}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'tenantId': tenantId
          }
        });
      } else {
        // Use original API endpoint for clinic feedback
        response = await axios.get(`${API_BASE}api/feedback?category=CLINIC`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'tenantId': tenantId
          }
        });
      }
      
      if (response.data) {
        // Handle paginated response for consultation, regular response for clinic
        if (activeTab === 'consultation') {
          // Consultation feedback response structure: { totalRecords, pageNumber, data: [], totalPages, pageSize }
          const feedbacksList = response.data.data || [];
          setFeedbacks(feedbacksList);
          setPagination({
            pageNumber: response.data.pageNumber || 0,
            totalPages: response.data.totalPages || 0,
            totalRecords: response.data.totalRecords || 0,
            pageSize: response.data.pageSize || 10
          });
        } else {
          // Clinic feedback response structure
          const allFeedbacks = response.data.content || response.data;
          const filteredFeedbacks = Array.isArray(allFeedbacks)
            ? allFeedbacks.filter(fb => fb.category === 'CLINIC' || !fb.category)
            : allFeedbacks;
          setFeedbacks(filteredFeedbacks);
        }
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setSubmitError('Failed to load feedbacks. Please try again later.');
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleTabChange = (tab) => {
    // Prevent USER role from accessing consultation tab
    if (userRole === 'USER' && tab === 'consultation') {
      return;
    }
    setActiveTab(tab);
    handleClear();
  };

  const handleRatingChange = (category, rating) => {
    setExperienceRatings(prev => ({
      ...prev,
      [category]: rating
    }));
  };

  const handleClear = () => {
    setFeedbackType('');
    setSubject('');
    setComments('');
    setCharCount(0);
    setExperienceRatings({
      systemReliability: 0,
      userInterface: 0,
      patientFlow: 0,
      bookingAppointments: 0,
      orderPlacement: 0,
      overallExperience: 0
    });
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const handleCommentsChange = (e) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setComments(value);
      setCharCount(value.length);
    }
  };

  const handleSubmit = async () => {
    if (!feedbackType) {
      setSubmitError('Please select feedback type');
      return;
    }

    if (!comments) {
      setSubmitError('Please enter your feedback');
      return;
    }

    if (feedbackType === 'EXPERIENCE') {
      const hasZeroRating = Object.values(experienceRatings).some(rating => rating === 0);
      if (hasZeroRating) {
        setSubmitError('Please rate all experience categories');
        return;
      }
    } else if (!subject) {
      setSubmitError('Please enter subject');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      const API_BASE = process.env.REACT_APP_API_URL;
  const clinicId = localStorage.getItem("clinicId");
      const payload = feedbackType === 'EXPERIENCE' 
        ? {
            clinicId: clinicId,
            type: feedbackType,
            category: activeTab === 'clinic' ? 'CLINIC' : 'CONSULTATION',
            ratings: experienceRatings,
            comments
          }
        : {
            clinicId: clinicId,
            type: feedbackType,
            category: activeTab === 'clinic' ? 'CLINIC' : 'CONSULTATION',
            subject,
            comments
          };

      const response = await axios.post(`${API_BASE}api/feedback`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'tenantId': tenantId
        }
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Feedback Submitted Successfully');
        setSubmitSuccess(true);
        handleClear();
        fetchFeedbacks();
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      setSubmitError(error.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar 
            key={star}
            color={star <= rating ? '#ffc107' : '#e4e5e9'} 
            className="text-lg"
          />
        ))}
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'IN-REVIEW':
        return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDefaultResponse = (type) => {
    switch (type) {
      case 'COMPLAINT':
        return 'We appreciate you bringing this to our attention. Our team will review your complaint and take appropriate action.';
      case 'SUGGESTION':
        return 'Thank you for your valuable suggestion. We will consider it for future improvements.';
      case 'EXPERIENCE':
        return 'Thank you for sharing your experience with us. Your feedback helps us improve our services.';
      case 'OTHERS':
        return 'Thank you for your feedback. We appreciate you taking the time to share your thoughts with us.';
      default:
        return 'Thank you for your feedback.';
    }
  };

  return (
    <OurLayouts>
      <div className="mx-auto p-6 bg-[#f1f8f7] rounded-lg shadow-md mt-10 w-[90%]">
        {/* <h1 className="text-2xl font-bold mb-6 text-[#004276]">Feedback Form</h1> */}
        
        {/* Tabs */}
        <div className="mb-6 border-b border-gray-300">
          <div className="flex space-x-1">
            <button
              onClick={() => handleTabChange('clinic')}
              className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-colors ${
                activeTab === 'clinic'
                  ? 'bg-gradient-to-r from-[#004172] to-[#3cba84] text-white border-b-2 border-[#004172]'
                  : 'text-gray-600 hover:text-[#004276] hover:bg-gray-100'
              }`}
            >
              Clinic Feedback
            </button>
            {userRole !== 'USER' && (
              <button
                onClick={() => handleTabChange('consultation')}
                className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-colors ${
                  activeTab === 'consultation'
                    ? 'bg-gradient-to-r from-[#004172] to-[#3cba84] text-white border-b-2 border-[#004172]'
                    : 'text-gray-600 hover:text-[#004276] hover:bg-gray-100'
                }`}
              >
                Consultation Feedback
              </button>
            )}
          </div>
        </div>
        
        {/* Feedback Form - Only show for Clinic tab */}
        {activeTab === 'clinic' && (
          <>
            {submitSuccess && (
              <div className="mb-4 p-4 bg-green-100 text-green-700 rounded">
                Thank you for your feedback! It has been submitted successfully.
              </div>
            )}

            {submitError && (
              <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
                {submitError}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="feedbackType" className="block text-lg font-medium mb-2 text-[#004276]">
                Feedback Type
              </label>
              <select 
                id="feedbackType"
                value={feedbackType}
                onChange={(e) => {
                  setFeedbackType(e.target.value);
                  setSubmitError('');
                }}
                className="w-full p-2.5 mb-5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select feedback type</option>
                <option value="EXPERIENCE">Experience</option>
                <option value="COMPLAINT">Complaint</option>
                <option value="SUGGESTION">Suggestion</option>
                <option value="OTHERS">Others</option>
              </select>
            </div>

            {feedbackType !== 'EXPERIENCE' && feedbackType !== '' && (
              <div className="mb-4">
                <label htmlFor="subject" className="block text-lg font-medium mb-2 text-[#004276]">
                  Subject
                </label>
                <input 
                  id="subject"
                  type="text" 
                  placeholder="Enter the subject" 
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full p-2.5 mb-5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            )}

            {feedbackType === 'EXPERIENCE' && (
              <div className="mb-5">
                <h2 className="text-lg font-medium mb-4 text-[#004276]">Rate Your Experience</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(experienceRatings).map(([category, rating]) => (
                    <div key={category} className="mb-4">
                      <p className="mb-2 text-[#004276] capitalize">
                        {category.split(/(?=[A-Z])/).join(' ')}
                      </p>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => handleRatingChange(category, star)}
                            className="text-2xl mr-2 focus:outline-none"
                          >
                            <FaStar 
                              color={star <= rating ? '#ffc107' : '#e4e5e9'} 
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="comments" className="block text-lg font-medium mb-2 text-[#004276]">
                Comments {charCount > 0 && <span className="text-sm text-gray-500">({charCount}/1000)</span>}
              </label>
              <textarea 
                id="comments"
                placeholder="Write your feedback (max 1000 characters)" 
                value={comments}
                onChange={handleCommentsChange}
                className="w-full h-[150px] p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
              {charCount === 1000 && (
                <p className="text-sm text-red-500 mt-1">Maximum character limit reached</p>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2.5">
              <button 
                onClick={handleClear}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Clear
              </button>
              <button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-5 py-2.5 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-md hover:from-[#00315a] hover:to-[#2da56f] disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </>
        )}

        <div className="mt-10">
          <h1 className="text-2xl font-bold mb-6 text-[#004276]">
            My {activeTab === 'clinic' ? 'Clinic' : 'Consultation'} Feedbacks
          </h1>
          
          {/* Date range picker for consultation feedback */}
          {activeTab === 'consultation' && (
            <div className="mb-6 flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label htmlFor="fromDate" className="block text-sm font-medium mb-2 text-[#004276]">
                  From Date
                </label>
                <input
                  id="fromDate"
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    const newFromDate = e.target.value;
                    if (newFromDate <= toDate) {
                      setFromDate(newFromDate);
                    } else {
                      toast.error('From date cannot be greater than to date');
                    }
                  }}
                  max={toDate}
                  className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label htmlFor="toDate" className="block text-sm font-medium mb-2 text-[#004276]">
                  To Date
                </label>
                <input
                  id="toDate"
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    const newToDate = e.target.value;
                    if (newToDate >= fromDate) {
                      setToDate(newToDate);
                    } else {
                      toast.error('To date cannot be less than from date');
                    }
                  }}
                  min={fromDate}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
          
          {loadingFeedbacks ? (
            <div className="text-center py-4">Loading feedbacks...</div>
          ) : feedbacks.length === 0 ? (
            <div className="text-center py-4">No feedbacks submitted yet</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {feedbacks.map((feedback) => (
                <div key={feedback.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                  {activeTab === 'consultation' ? (
                    // Consultation Feedback Display
                    <div className="mt-2">
                      <div className="flex justify-between items-start mb-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded capitalize">
                          {feedback.areaOfExperience || 'Consultation'}
                        </span>
                        {feedback.createdDate && (
                          <span className="text-xs text-gray-500">
                            {new Date(feedback.createdDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-500">Patient Name</label>
                        <p className="font-semibold text-[#004276]">{feedback.patientName || 'N/A'}</p>
                      </div>
                      
                      {feedback.whatsappNumber && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-500">WhatsApp Number</label>
                          <p className="text-gray-600">{feedback.whatsappNumber}</p>
                        </div>
                      )}
                      
                      {(feedback.q1 || feedback.q2 || feedback.q3 || feedback.q4 || feedback.q5) && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-500 mb-2">Ratings</label>
                          <div className="space-y-2">
                            {feedback.q1 && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[#004276]">Q1:</span>
                                {renderStars(feedback.q1)}
                              </div>
                            )}
                            {feedback.q2 && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[#004276]">Q2:</span>
                                {renderStars(feedback.q2)}
                              </div>
                            )}
                            {feedback.q3 && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[#004276]">Q3:</span>
                                {renderStars(feedback.q3)}
                              </div>
                            )}
                            {feedback.q4 && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[#004276]">Q4:</span>
                                {renderStars(feedback.q4)}
                              </div>
                            )}
                            {feedback.q5 && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[#004276]">Q5:</span>
                                {renderStars(feedback.q5)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {feedback.overallFeedback && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-500">Overall Feedback</label>
                          <p className="text-gray-600 mt-1 italic">"{feedback.overallFeedback}"</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Clinic Feedback Display (existing code)
                    <>
                      <div className="flex justify-between items-start mb-3">
                        <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(feedback.status || 'SUBMITTED')}`}>
                          {feedback.status || 'SUBMITTED'}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded capitalize">
                          {feedback.type?.toLowerCase() || 'feedback'}
                        </span>
                      </div>
                  
                  {feedback.type === 'COMPLAINT' && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-500">Subject</label>
                      <h3 className="font-semibold mb-2">{feedback.subject}</h3>
                      
                      <label className="block text-sm font-medium text-gray-500">Comments</label>
                      <p className="text-gray-600 mt-1 mb-3">{feedback.comments}</p>
                      
                      <label className="block text-sm font-medium text-gray-500">Response</label>
                      <div className="mt-1 p-2 bg-gray-50 rounded">
                        <p className="text-gray-600">
                          {feedback.adminResponse || getDefaultResponse(feedback.type)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {feedback.type === 'SUGGESTION' && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-500">Subject</label>
                      <h3 className="font-semibold mb-2">{feedback.subject}</h3>
                      
                      <label className="block text-sm font-medium text-gray-500">Comments</label>
                      <p className="text-gray-600 mt-1 mb-3">{feedback.comments}</p>
                      
                      <label className="block text-sm font-medium text-gray-500">Response</label>
                      <div className="mt-1 p-2 bg-gray-50 rounded">
                        <p className="text-gray-600">
                          {feedback.adminResponse || getDefaultResponse(feedback.type)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {feedback.type === 'EXPERIENCE' && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-500">Comments</label>
                      <p className="text-gray-600 italic mb-3">"{feedback.comments}"</p>
                      
                      {feedback.ratings && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Ratings</label>
                          <div className="mt-1 space-y-2">
                            {Object.entries(feedback.ratings).map(([category, rating]) => (
                              <div key={category} className="flex items-center justify-between">
                                <span className="text-sm text-[#004276] capitalize">
                                  {category.split(/(?=[A-Z])/).join(' ')}:
                                </span>
                                {renderStars(rating)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <label className="block text-sm font-medium text-gray-500 mt-3">Response</label>
                      <div className="mt-1 p-2 bg-gray-50 rounded">
                        <p className="text-gray-600">
                          {feedback.adminResponse || getDefaultResponse(feedback.type)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {feedback.type === 'OTHERS' && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-500">Subject</label>
                      <h3 className="font-semibold mb-2">{feedback.subject}</h3>
                      
                      <label className="block text-sm font-medium text-gray-500">Comments</label>
                      <p className="text-gray-600 mt-1 mb-3">{feedback.comments}</p>
                      
                      <label className="block text-sm font-medium text-gray-500">Response</label>
                      <div className="mt-1 p-2 bg-gray-50 rounded">
                        <p className="text-gray-600">
                          {feedback.adminResponse || getDefaultResponse(feedback.type)}
                        </p>
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination for consultation feedback */}
          {activeTab === 'consultation' && pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center items-center gap-4">
              <button
                onClick={() => fetchFeedbacks(pagination.pageNumber - 1, pagination.pageSize)}
                disabled={pagination.pageNumber === 0}
                className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.pageNumber + 1} of {pagination.totalPages} ({pagination.totalRecords} total)
              </span>
              <button
                onClick={() => fetchFeedbacks(pagination.pageNumber + 1, pagination.pageSize)}
                disabled={pagination.pageNumber >= pagination.totalPages - 1}
                className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </OurLayouts>
  );
};

export default FeedbackPage;